import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { GraphQLFormattedError } from 'graphql';
import _get from 'lodash/get';
import Modal from '@lib/components/Modal/Modal';
import Form from '@lib/components/ReactHookForm/FormContainer';
import Input from '@lib/components/Input/Input';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import { FieldItem, Values } from '@lib/interfaces/form';
import { getFieldValidation } from '@lib/utils/validationUtils';

const TITLE_FIELD = 'title';

type ActionsProps = {
  loading?: boolean;
  onCancel: () => void;
};

function RenameActions({ loading, onCancel }: ActionsProps) {
  const { t } = useTranslation();
  return (
    <ActionsRow
      actions={[
        <Button
          key="cancel"
          buttonType={ButtonTypes.secondaryOutlined}
          buttonText={t('cancel')}
          leftIcon="close"
          onClick={onCancel}
          fullWidth
          disabled={loading}
        />,
        <Button
          key="submit"
          type="submit"
          buttonType={ButtonTypes.primaryFilled}
          buttonText={t('save-changes')}
          leftIcon="check"
          fullWidth
          disabled={loading}
        />,
      ]}
    />
  );
}

function getRenameFields(t: TFunction<'translation', undefined>): FieldItem[] {
  return [
    {
      name: TITLE_FIELD,
      label: t('name'),
      element: Input,
      validation: getFieldValidation({ t, required: true }),
      componentProps: {
        autoFocus: true,
      },
    },
  ];
}

type Props = {
  isOpen: boolean;
  // Re-keys the form so it re-initialises with the right title on every open.
  formKey: string;
  currentTitle?: string | null;
  loading?: boolean;
  graphQLErrors?: readonly GraphQLFormattedError[];
  onClose: () => void;
  onSubmit: (title: string) => void;
};

function AiRenameChatModal(props: Props) {
  const {
    isOpen,
    formKey,
    currentTitle,
    loading,
    graphQLErrors,
    onClose,
    onSubmit,
  } = props;
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} title={t('ai-rename-chat')} onClose={onClose}>
      <Form
        key={formKey}
        defaultValues={{ [TITLE_FIELD]: currentTitle ?? '' }}
        fields={getRenameFields(t)}
        onSubmit={(values: Values) =>
          onSubmit(String(_get(values, TITLE_FIELD, '')).trim())
        }
        actionsComponent={RenameActions}
        actionsComponentProps={{ loading, onCancel: onClose }}
        graphQLErrors={graphQLErrors}
        loading={loading}
      />
    </Modal>
  );
}

export default AiRenameChatModal;
